import { useCalendar } from "../../context/hook";
import {
  addMinutes,
  isSameDay,
  isToday,
  isAfter,
  isBefore,
  endOfDay,
  startOfDay,
} from "date-fns";
import { cn } from "../../../../lib/utils";
import { DateInPeriod } from "../../context/types";
import { ActivityFragment } from "../../../../__generated__/graphql";
import { useRef, useMemo } from "react";
import { ActivityPosition } from "./types";
import ActivityItem from "./components/ActivityItem";
import { motion } from "motion/react";

const HOURS_IN_DAY = 24;
const MINUTES_IN_DAY = 1440;

// This component is heavily relying on AI
export default function WeeklyCalendar() {
  const { datesInPeriod, events, activities } = useCalendar();
  const dayColumnRef = useRef<HTMLDivElement>(null);

  const allActivities = useMemo(() => {
    // We have activities under events and some directly in the activities array
    // As we are querying only the event within the current period, we might miss some activities
    // ex: Event the 18th of June, but the activity is the next week
    return (
      [
        ...(events?.flatMap((event) => event.activities || []) || []),
        ...(activities || []),
      ]
        .filter((activity) => activity !== null)
        // Remove duplicates
        .filter(
          (activity, index, self) =>
            index === self.findIndex((t) => t.id === activity.id)
        )
    );
  }, [events, activities]);

  const calculateActivityPositions = (
    activities: ActivityFragment[],
    currentDay: Date
  ): ActivityPosition[] => {
    if (!activities?.length) return [];

    // First, calculate basic positions for all activities
    const positions = activities.map((activity) => {
      const startDate = new Date(activity.startDate);
      const endDate = activity.endDate
        ? new Date(activity.endDate)
        : addMinutes(startDate, 30);

      // If activity spans multiple days, adjust start/end times for current day
      let adjustedStartDate = startDate;
      let adjustedEndDate = endDate;

      if (!isSameDay(startDate, currentDay)) {
        adjustedStartDate = startOfDay(currentDay);
      }
      if (!isSameDay(endDate, currentDay)) {
        adjustedEndDate = endOfDay(currentDay);
      }

      const startHour = adjustedStartDate.getHours();
      const startMinutes = adjustedStartDate.getMinutes();
      const endHour = adjustedEndDate.getHours();
      const endMinutes = adjustedEndDate.getMinutes();

      const top = ((startHour * 60 + startMinutes) / MINUTES_IN_DAY) * 100;
      const height =
        ((endHour * 60 + endMinutes - (startHour * 60 + startMinutes)) /
          MINUTES_IN_DAY) *
        100;

      return {
        ...activity,
        activity,
        startDate: adjustedStartDate,
        endDate: adjustedEndDate,
        top,
        height,
        left: 2,
        width: 96, // Default width (100% - 4px)
      };
    });

    // Sort activities by start time
    positions.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    // Group overlapping activities
    const groups: ActivityPosition[][] = [];
    let currentGroup: ActivityPosition[] = [];

    positions.forEach((activity, index) => {
      if (currentGroup.length === 0) {
        currentGroup.push(activity);
      } else {
        const lastActivity = currentGroup[currentGroup.length - 1];
        const lastEnd =
          lastActivity.startDate.getTime() +
          (lastActivity.height / 100) * MINUTES_IN_DAY * 60000;
        const currentStart = activity.startDate.getTime();

        if (currentStart < lastEnd) {
          // Activities overlap
          currentGroup.push(activity);
        } else {
          // No overlap, start new group
          groups.push([...currentGroup]);
          currentGroup = [activity];
        }
      }

      if (index === positions.length - 1) {
        groups.push(currentGroup);
      }
    });

    // Adjust positions within each group
    groups.forEach((group) => {
      const groupWidth = 96; // Total available width
      const itemWidth = groupWidth / group.length;

      group.forEach((activity, index) => {
        activity.width = itemWidth;
        activity.left = 2 + index * itemWidth;
      });
    });

    return positions;
  };

  // Calculate activity positions for each day
  const activityPositionsByDay = useMemo(() => {
    return datesInPeriod.reduce((acc, day) => {
      // Get all activities that overlap with this day
      const activitiesInDay =
        allActivities.filter((activity) => {
          if (!activity?.startDate) return false;
          const activityStart = new Date(activity.startDate);
          const activityEnd = activity.endDate
            ? new Date(activity.endDate)
            : addMinutes(activityStart, 30);

          // Activity overlaps with current day if:
          // 1. It starts on this day, or
          // 2. It ends on this day, or
          // 3. It spans across this day
          return (
            isSameDay(activityStart, day.date) ||
            isSameDay(activityEnd, day.date) ||
            (isBefore(activityStart, endOfDay(day.date)) &&
              isAfter(activityEnd, startOfDay(day.date)))
          );
        }) || [];

      acc[day.date.toISOString()] = calculateActivityPositions(
        activitiesInDay,
        day.date
      );
      return acc;
    }, {} as Record<string, ActivityPosition[]>);
  }, [datesInPeriod, allActivities]);

  const weekDay = (day: DateInPeriod) => {
    return (
      <div
        key={`${day.formatted}-${day.date.toISOString()}`}
        className={cn(
          "text-center select-none text-secondary text-sm font-semibold",
          isToday(day.date) && "text-accent"
        )}
      >
        {day.formatted}
      </div>
    );
  };

  const TimeGrid = () => (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: HOURS_IN_DAY + 1 }).map((_, hour) => (
        <div
          key={hour}
          className="absolute w-full border-t border-input"
          style={{
            top: `${((hour * 60) / MINUTES_IN_DAY) * 100}%`,
          }}
        />
      ))}
    </div>
  );

  const TimeColumn = () => (
    <div className="flex-shrink-0 relative w-10">
      <div className="absolute inset-0 border-r border-border">
        {Array.from({ length: HOURS_IN_DAY }).map((_, hour) => {
          if (hour === 0) return null;

          return (
            <div
              key={hour}
              className="absolute w-full flex items-center justify-end text-secondary text-xs pr-1"
              style={{
                top: `${((hour * 60) / MINUTES_IN_DAY) * 100}%`,
                transform: "translateY(-50%)",
              }}
            >
              {hour.toString().padStart(2, "0")}:00
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <motion.div
      key={datesInPeriod.map((day) => day.date.toISOString()).join("-")}
      className="flex flex-col h-[150vh]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex sticky top-0 z-5">
        <div className="w-10" /> {/* Spacer for time column */}
        {datesInPeriod.map((day) => {
          return (
            <div
              key={`${day.formatted}-${day.date.toISOString()}`}
              className="w-[14.28%] flex flex-col justify-between"
            >
              {weekDay(day)}
            </div>
          );
        })}
      </div>

      <div className="flex-1 border border-border rounded-md">
        <div className="flex h-full rounded-md border-b border-border">
          <TimeColumn />

          {datesInPeriod.map((day) => {
            const isLast = day.date.getDay() === 7 || day.date.getDay() === 0;
            const activityPositions =
              activityPositionsByDay[day.date.toISOString()] || [];

            return (
              <div
                key={`${day.formatted}-${day.date.toISOString()}`}
                className={cn(
                  "w-[14.28%] relative px-0.5",
                  !isLast && "border-r border-border"
                )}
              >
                <TimeGrid />

                <div ref={dayColumnRef} className="flex flex-col h-full">
                  {activityPositions.map((activity) => (
                    <ActivityItem
                      key={activity.id}
                      activity={activity}
                      top={activity.top}
                      height={activity.height}
                      width={activity.width}
                      left={activity.left}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
